import React, { useState, useEffect } from "react";
import { Form, Input, Spin } from "antd";
import { logIn } from "../../utils/api";
import { LoadingOutlined } from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import GoogleSignInButton from "./GoogleSignInButton";

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notMatchedError, setNotMatchedError] = useState("");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    // Check for logout message from session invalidation (single-device login)
    const logoutMessage = sessionStorage.getItem("logoutMessage");
    if (logoutMessage) {
      setNotMatchedError(logoutMessage);
      sessionStorage.removeItem("logoutMessage");
    }
  }, []);

  const onFinish = (values) => {
    const cleanedValues = {
      password: values.password.trim(),
      email: values.email.trim(),
    };
    setLoading(true);
    logIn(cleanedValues)
      .then((res) => {
        if (res?.data) {
          localStorage.setItem("token", res?.data?.data?.accessToken);
          if (res?.data?.data?.role) {
            localStorage.setItem("role", res?.data?.data?.role);
          }
          if (res?.data?.data?.role === "manager") {
            navigate("/profile");
          } else {
            navigate("/form_page");
          }
        }
        if (res?.response?.data?.statusCode === 400) {
          setNotMatchedError(res?.response?.data?.message);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <Spin
      spinning={loading}
      indicator={
        <LoadingOutlined
          style={{
            fontSize: 48,
            color: "white",
          }}
          spin
        />
      }
    >
      <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

        <div className="relative min-h-screen flex items-center justify-center px-4 py-12 pt-32">
          <div
            className={`w-full max-w-md transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {/* Card */}
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 md:p-10 shadow-2xl">
              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
                <p className="text-gray-400">Sign in to continue to your account</p>
              </div>

              <Form
                name="login"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
                layout="vertical"
                className="space-y-2"
              >
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input
                    placeholder="Email address"
                    className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
                    style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white" }}
                  />
                </Form.Item>

                <Form.Item name="password" rules={[{ required: true, message: "Please enter your password" }]}>
                  <Input.Password
                    placeholder="Password"
                    className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
                    style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white" }}
                  />
                </Form.Item>

                <div className="flex justify-end mb-2">
                  <Link to="/forgot-password" className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
                    Forgot password?
                  </Link>
                </div>

                {notMatchedError && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 mb-4">
                    <p className="text-red-300 text-sm text-center">{notMatchedError}</p>
                  </div>
                )}

                <Form.Item className="mb-0">
                  <button
                    type="submit"
                    className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transform hover:-translate-y-0.5"
                  >
                    Sign In
                  </button>
                </Form.Item>
              </Form>

              {/* Divider */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-transparent text-gray-400">or continue with</span>
                </div>
              </div>

              {/* Social Login */}
              <GoogleSignInButton
                onSuccess={() => navigate("/form_page")}
                onError={(error) => setNotMatchedError(error)}
                buttonText="Continue with Google"
                fullWidth
              />

              {/* Sign Up Link */}
              <p className="text-center text-gray-400 mt-8">
                Don't have an account?{" "}
                <Link to="/register" className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors">
                  Sign up free
                </Link>
              </p>
            </div>

            {/* Trust badges */}
            <div
              className={`flex justify-center items-center gap-6 mt-8 text-gray-500 text-sm transition-all duration-700 delay-300 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                <span>Secure login</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <span>256-bit encryption</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default Login;
